import React, { useEffect, useState, Dispatch, useMemo } from 'react';
import { SearchTable, SingleSelectFilter } from '@kaleyra/emmy';
import { Link } from 'react-router-dom';
import { v2Whatsapp } from '../../../../../urls';
import { useTranslation } from 'react-i18next';
import LogFilter from '../logs/filter';
import { useDispatch, useSelector } from 'react-redux';
import Styles from './index.module.scss';
import { debounce, isEmpty } from 'lodash';
import cn from 'classnames';
import { IWhatsappLogs } from '../../../../../store/models/whatsappLogs.interface';
import {
	disableWabaIdDropdown,
	enableWabaIdDropdown,
} from '../../../../../store/actions/wabaIdDropdown.action';
import { IStateType, IWabaIdState } from '../../../../../store/models/root.interface';
import {
	MessageLogsV1,
	expandColumnMessageV1Body,
	updateFiltersForV1,
} from '../logs/schemas/messageLogsV1';
import {
	VoiceLogs,
	expandColumnVoiceBody,
	updateFiltersForVoice,
} from '../logs/schemas/voiceLogs';
import { OrderingRuleType } from '../../../../../interface';
import { REACT_APP_ENABLE_WA_VOICE_LOGS } from '../../../../../base';

export interface IOption {
	label: string;
	value: string;
}

export type MapStateType = {
	appliedFilters: Record<string, any>;
	data: any;
	wabaID: IWabaIdState;
	logsData: IWhatsappLogs[];
	v2LogsData: any;
	voiceData: any;
	page: number;
	v2Page: number;
	voicePage: number;
	total: number;
	v2Total: number;
	voiceTotal: number;
	inProgress: boolean;
	voiceInProgress: boolean;
	v2InProgress: boolean;
};

export type DispatchPropsType = {
	onFilter: (
		filters: Record<string, any> | undefined,
		logsFor: string
	) => void;
	onSearchStrChange: (logsFor: string, searchStr?: string) => void;
	onLoad: (logsFor: string, wabaId: string | number) => void;
	onPaginate: (logsFor: string) => void;
	onLoadData: (logsFor: string, wabaId: string | number) => void;
	onSort: (logsFor: string, sort: OrderingRuleType[]) => void;
};

const Logs: React.FC<MapStateType & DispatchPropsType> = ({
	appliedFilters,
	data,
	logsData,
	v2LogsData,
	voiceData,
	inProgress,
	v2InProgress,
	voiceInProgress,
	wabaID,
	page,
	v2Page,
	voicePage,
	total,
	v2Total,
	voiceTotal,
	onFilter,
	onPaginate,
	onLoad,
	onLoadData,
	onSearchStrChange,
	onSort,
}) => {
	const [searchText, setSearchText] = useState<string>('');
	const { t } = useTranslation();
	const [tableProperties, setTableProperties] = useState<any>({
		tableColums: () => [],
		expandTable: () => null,
		tableData: [],
		tablePage: 0,
		tableTotal: 0,
		inProgress: false,
	});
	const profileDetails = useSelector((state: IStateType) => state.profile);
    const { company_id } = profileDetails.fullProfile
	const voiceEnabledCompanyId = REACT_APP_ENABLE_WA_VOICE_LOGS ? REACT_APP_ENABLE_WA_VOICE_LOGS.split(',') : [];
	const [logsFor, setLogsFor] = useState<string>('messageLogs');
	const dispatch: Dispatch<any> = useDispatch();
	const { whatsappLogsData,  whatsappVoiceLogsData } = data;
	const [selFilters, setSelFilters] = useState<any>({});
	const [enbaleVoiceTab, setEnbaleVoiceTab] = useState<boolean>(false);
	const [appliedFilterText, setAppliedFilterText] = useState<
		Record<string, string>
	>({} as Record<string, string>);
	const { wabaId, version } = wabaID;
	const [sortOrder, setSortOrder] = useState<Array<OrderingRuleType>>([]);

	useEffect(() => {
		dispatch(enableWabaIdDropdown(false));

		return () => {
			dispatch(disableWabaIdDropdown(false));
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (wabaId && logsFor && version) {
			if(voiceEnabledCompanyId.includes(`${company_id}_${wabaId}`)){
				setEnbaleVoiceTab(true);
			}else{
				setEnbaleVoiceTab(false);
				setLogsFor("messageLogs");
			}
			onLoad(`${logsFor}V${version}`, wabaId);
			onLoadData(`${logsFor}V${version}`, wabaId);
			if (!isEmpty(selFilters)) clearFilters();
			if (searchText) {
				setSearchText('');
				onSearchStrChange(`${logsFor}V${version}`, '');
			}
		}
	}, [wabaId, logsFor, version]);

	//TODO: Try to implement useMemo to memoize results for diff channels

	useEffect(() => {
		switch (`${logsFor}V${version}`) {
			case 'messageLogsV1':
				setTableProperties({
					expandTable: expandColumnMessageV1Body,
					tableColums: MessageLogsV1,
					tableData: logsData,
					tablePage: page,
					tableTotal: total,
					inProgress: inProgress,
				})
				break;
			case 'messageLogsV2':
				setTableProperties({
					expandTable: expandColumnMessageV1Body,
					tableColums: MessageLogsV1,
					tableData: v2LogsData,
					tablePage: v2Page,
					tableTotal: v2Total,
					inProgress: v2InProgress,
				});
				break;
			case 'voiceLogsV1':
				setTableProperties({
					expandTable: expandColumnVoiceBody,
					tableColums: VoiceLogs,
					tableData: voiceData,
					tablePage: voicePage,
					tableTotal: voiceTotal,
					inProgress: voiceInProgress,
				});
				break;
				case 'voiceLogsV2':
					setTableProperties({
						expandTable: expandColumnVoiceBody,
						tableColums: VoiceLogs,
						tableData: voiceData,
						tablePage: voicePage,
						tableTotal: voiceTotal,
						inProgress: voiceInProgress,
					});
					break;
			default:
				setTableProperties({
					tableColums: () => [],
					tableData: [],
					tablePage: 0,
					tableTotal: 0,
				});
				break;
		}
	}, [logsData, v2LogsData, voiceData]);

	const getOptions = (data: any): IOption[] => {
		let options: IOption[] = [];
		let keyOptions: Record<string, any> = { ...data };

		Object.keys(keyOptions).forEach((key: string) => {
			if (key)
				options.push({
					value: key,
					label: keyOptions[key],
				});
		});
		return options;
	};

	const statusOptions = useMemo(() => {
		return getOptions(whatsappLogsData?.data?.status);
	}, [whatsappLogsData?.data?.status]);

	const formatOptions = useMemo(() => {
		return getOptions(whatsappLogsData?.data?.format);
	}, [whatsappLogsData?.data?.format]);

	const conversationCategories = useMemo(() => {
		return getOptions(whatsappLogsData?.data?.conversation_category);
	}, [whatsappLogsData?.data?.conversation_category]);

	const callType = useMemo(() => {
		return getOptions(whatsappVoiceLogsData?.data?.type);
	}, [whatsappVoiceLogsData?.data?.type]);

	const voiceStatusOptions = useMemo(() => {
		return getOptions(whatsappVoiceLogsData?.data?.status);
	}, [whatsappVoiceLogsData?.data?.status]);

	const onSearch = (logsFor: string, searchStr: string = '') => {
		onSearchStrChange(logsFor, searchStr);
	};

	const updateFilters = (filters: any, remove?: boolean) => {
		let appliedOb = {};

		switch (logsFor) {
			case 'voiceLogs':
				appliedOb = updateFiltersForVoice(
					filters,
					selFilters,
					whatsappVoiceLogsData,
					remove
				);
				break;
			case 'messageLogs':
				appliedOb = updateFiltersForV1(
					filters,
					selFilters,
					whatsappLogsData,
					remove
				);
				break;
			default:
				appliedOb = { ...filters };
				break;
		}
		setAppliedFilterText(remove ? filters : appliedOb);
		onFilter(
			remove
				? { wabaID: wabaId, ...appliedOb }
				: { wabaID: wabaId, ...filters },
			`${logsFor}V${version}`
		);
		setSelFilters(remove ? appliedOb : filters);
	};

	const sortFunction = (column: any) => {
		let order = sortOrder?.find(
			({ id }) => id === column[column.length - 1].id
		);
		if (order) {
			setSortOrder([{ id: column[column.length - 1].id, desc: true }]);
			onSort(`${logsFor}V${version}`, [{ id: column[column.length - 1].id, desc: true }]);
		} else if (!isEmpty(column)) {
			setSortOrder([{ id: column[column.length - 1].id }]);
			onSort(`${logsFor}V${version}`, [{ id: column[column.length - 1].id }]);
		} else {
			setSortOrder([]);
			onSort(`${logsFor}V${version}`, []);
		}
	};

	const clearFilters = () => {
		setAppliedFilterText({});
		setSelFilters({});
		onFilter({ wabaID: wabaId }, `${logsFor}V${version}`);
	};

	const debouncedChange = React.useCallback(
		debounce((logsFor: string, version: number, value: string) => {
			setSearchText(value);
			onSearch(`${logsFor}V${version}`, value);
		}, 400),
		[]
	);

	return (
		<div className={Styles.whatsappLogs}>
			<div className={Styles.whatsappAnalyticsTab}>
				<Link to={v2Whatsapp({ tab: 'logs' })}>
					<div
						className={cn(
							Styles.chip,
							window.location.pathname.includes(
								v2Whatsapp({ tab: 'logs' })
							)
								? Styles.chipActive
								: null
						)}
					>
						{t('tabLogs')}
					</div>
				</Link>
				<Link to={v2Whatsapp({ tab: 'reports' })}>
					<div className={Styles.chip}>{t('tabReports')}</div>
				</Link>
			</div>
			<SearchTable.SearchTable
				manual
				canSort
				enableSearch
				onFilter={(e: any, remove?: boolean) => {
					updateFilters(e, remove);
				}}
				onSearch={(val: string) => {
					debouncedChange(logsFor, version, val);
				}}
				searchText={searchText}
				page={tableProperties?.tablePage + 1}
				pageSize={25}
				total={tableProperties?.tableTotal}
				onPaginate={() => onPaginate(`${logsFor}V${version}`)}
				filters={appliedFilterText}
				inProgress={tableProperties?.inProgress}
				searchPlaceholder={t('searchWALogs')}
				searchLabel={t('searchWALogs')}
				onSort={sortFunction}
				ordering={sortOrder}
				filterComponents={(filters, tableOnFilter) => (
					<div className={Styles.waChannelWiseFilter}>
						{enbaleVoiceTab && <SingleSelectFilter
							onSelect={(e) => setLogsFor(e)}
							defaultValue={{
								key: 'messageLogs',
								value: 'Message Logs',
							}}
							options={[
								 { key: 'messageLogs', value: 'Message Logs' },
								 { key: 'voiceLogs', value: 'Voice Logs' },
							]}
						/>}
						<LogFilter
							appliedFilters={selFilters || {}}
							onFilter={tableOnFilter}
							conversationCategories={conversationCategories}
							statusOptions={statusOptions}
							formatOptions={formatOptions}
							logsFor={logsFor}
							callType={callType}
							voiceStatusOptions={voiceStatusOptions}
						/>
					</div>
				)}
				clearAllFilters={clearFilters}
				rowExpand
				SubComponent={tableProperties?.expandTable}
				data={tableProperties?.tableData}
				columns={tableProperties?.tableColums() || []}
			/>
		</div>
	);
};

export default Logs;
